import { yupResolver } from '@hookform/resolvers/yup'
import { FC, useCallback, useEffect, useState } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import * as yup from 'yup'

import {
  FinancialSettingsCategory,
  FinancialSettingsTax,
} from '../../../../common/models/settings'
import FormGroup from '../../../../core/components/FormGroup'
import { useCheckPermission } from '../../../../core/hooks/use-check-permission'
import {
  createFinancialSettingsCategory,
  updateFinancialSettingsCategory,
  deleteFinancialSettingsCategory,
  getTaxCategories,
  getFinancialSettingsCategory,
  getFinancialSettingsCategoryTickets,
} from '../../../../core/services/festival-settings'
import { DeleteConfirmationModal } from '../../DeleteConfirmationModal'
import {
  CategoryTicket,
  SaveConfirmationModal,
} from '../../SaveConfirmationModal'
import { MessageBox } from '../../messages/MessageBox'
import { Button, Checkbox, Input } from '@/components/ui'
import { TaxItem } from './TaxItem'

const taxSchema = yup.object().shape({
  id: yup.string(),
  name: yup.string().required('Tax name is required'),
  amountPercentage: yup.number().nullable(),
  amountFixed: yup.number().nullable(),
  vat: yup.boolean().required(),
  includeInInvoice: yup.boolean().required(),
  includedInTicketPrice: yup.boolean().required(),
})

const categorySchema = yup.object().shape({
  id: yup.string(),
  name: yup.string().required('Category name is required'),
  active: yup.boolean().required(),
  taxes: yup.array().of(taxSchema).required(),
})

const schema = yup.object().shape({
  categories: yup.array().of(categorySchema).required(),
})

type ITaxFormValues = yup.InferType<typeof schema>

export const TaxCategoriesSection: FC = () => {
  const [isError, setIsError] = useState(false)
  const [showMessageBox, setShowMessageBox] = useState(false)
  const [message, setMessage] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<{
    type: 'category' | 'tax'
    categoryIndex: number
    taxIndex?: number
  } | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showSaveModal, setShowSaveModal] = useState(false)
  const [pendingSaveCategoryIndex, setPendingSaveCategoryIndex] = useState<
    number | null
  >(null)
  const [saveTaxName, setSaveTaxName] = useState<string>()
  const [saveTickets, setSaveTickets] = useState<CategoryTicket[]>([])
  const [savingCategoryIndex, setSavingCategoryIndex] = useState<number | null>(
    null,
  )

  const { hasEditPermission } = useCheckPermission({ page: 'SETTINGS' })

  const getDataTaxCategories = useCallback(async () => {
    try {
      const { data } = await getFinancialSettingsCategory()
      reset({
        categories: data,
      })
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      }
    }
  }, [])

  useEffect(() => {
    getDataTaxCategories()
  }, [getDataTaxCategories])

  const {
    register,
    formState: { errors },
    setValue,
    watch,
    control,
    reset,
    getValues,
  } = useForm<ITaxFormValues>({
    mode: 'onChange',
    resolver: yupResolver<ITaxFormValues>(schema),
    defaultValues: { categories: [] },
  })

  const {
    fields: categoryFields,
    append: appendCategory,
    remove: removeCategory,
    update: updateCategory,
  } = useFieldArray({
    control,
    name: 'categories',
  })

  const handleSaveCategory = useCallback(
    async (categoryIndex: number) => {
      const category = getValues(`categories.${categoryIndex}`)
      setSavingCategoryIndex(categoryIndex)
      try {
        const payload: FinancialSettingsCategory = {
          id: category.id || '',
          name: category.name,
          active: category.active,
          taxes: category.taxes.map(
            (t) =>
              ({
                id: t.id || '',
                name: t.name,
                amountPercentage: t.amountPercentage ?? 0,
                amountFixed: t.amountFixed ?? 0,
                vat: t.vat,
                includeInInvoice: t.includeInInvoice,
                includedInTicketPrice: t.includedInTicketPrice,
              } satisfies FinancialSettingsTax),
          ),
        }

        let status: number
        if (category.id) {
          const res = await updateFinancialSettingsCategory(
            category.id,
            payload,
          )
          status = res.status
        } else {
          const res = await createFinancialSettingsCategory(payload)
          status = res.status
          if (res.data?.id) {
            updateCategory(categoryIndex, {
              ...category,
              id: res.data.id,
            })
          }
        }

        if (status === 200 || status === 201) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Update Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        }
      } finally {
        setSavingCategoryIndex(null)
      }
    },
    [getValues, updateCategory],
  )

  const handleRequestSave = useCallback(
    async (categoryIndex: number) => {
      const category = getValues(`categories.${categoryIndex}`)
      setPendingSaveCategoryIndex(categoryIndex)
      setSaveTaxName(category.name)

      if (category.id) {
        try {
          const { data } = await getFinancialSettingsCategoryTickets(
            category.id,
          )
          setSaveTickets(Array.isArray(data) ? data : [data])
        } catch {
          setSaveTickets([])
        }
      } else {
        setSaveTickets([])
      }

      setShowSaveModal(true)
    },
    [getValues],
  )

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return
    if (deleteTarget.type === 'category') {
      const category = getValues(`categories.${deleteTarget.categoryIndex}`)
      if (category.id) {
        try {
          await deleteFinancialSettingsCategory(category.id)
        } catch (e) {
          if (e instanceof Error) {
            setIsError(true)
            setShowMessageBox(true)
            setMessage(e.message)
          }
          setDeleteTarget(null)
          return
        }
      }
      removeCategory(deleteTarget.categoryIndex)
    }
    setDeleteTarget(null)
  }, [deleteTarget, removeCategory, getValues])

  const handleVatChange = useCallback(
    (categoryIndex: number, taxIndex: number, checked: boolean) => {
      if (checked) {
        const taxes = watch(`categories.${categoryIndex}.taxes`)
        taxes.forEach((_: unknown, i: number) => {
          if (i !== taxIndex) {
            setValue(`categories.${categoryIndex}.taxes.${i}.vat`, false)
          }
        })
      }
      setValue(`categories.${categoryIndex}.taxes.${taxIndex}.vat`, checked)
    },
    [watch, setValue],
  )

  return (
    <>
      <div className="rounded-3xl border border-monochrome-120 bg-gradient-4 mt-[25px]">
        <div className="flex flex-col gap-[25px] p-[25px]">
          <h2 className="text-title-xs text-monochrome-1">Tax Categories</h2>
          {categoryFields.map((categoryField, categoryIndex) => (
            <CategoryItem
              key={categoryField.id}
              categoryIndex={categoryIndex}
              register={register}
              errors={errors}
              control={control}
              watch={watch}
              setValue={setValue}
              hasEditPermission={hasEditPermission}
              onDeleteCategory={() => {
                setDeleteTarget({
                  type: 'category',
                  categoryIndex,
                })
                setShowDeleteModal(true)
              }}
              onSaveCategory={() => handleRequestSave(categoryIndex)}
              isSaving={savingCategoryIndex === categoryIndex}
              onVatChange={handleVatChange}
            />
          ))}
          <Button
            variant="secondary"
            type="button"
            onClick={() =>
              appendCategory({
                name: '',
                active: true,
                taxes: [],
              })
            }
            disabled={!hasEditPermission}
            className="self-center"
          >
            Add New Category +
          </Button>
        </div>
      </div>
      <SaveConfirmationModal
        open={showSaveModal}
        onOpenChange={setShowSaveModal}
        taxName={saveTaxName}
        tickets={saveTickets}
        onConfirm={() => {
          if (pendingSaveCategoryIndex !== null) {
            handleSaveCategory(pendingSaveCategoryIndex)
          }
          setPendingSaveCategoryIndex(null)
        }}
      />
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteModal}
        setDeleteConfirmationModal={setShowDeleteModal}
        deleteTitle={deleteTarget?.type === 'category' ? 'Category' : 'Tax'}
        deleteFunc={handleDelete}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}

// --- CategoryItem sub-component ---

interface CategoryItemProps {
  categoryIndex: number
  register: any
  errors: any
  control: any
  watch: any
  setValue: any
  hasEditPermission: boolean
  onDeleteCategory: () => void
  onSaveCategory: () => void
  isSaving: boolean
  onVatChange: (
    categoryIndex: number,
    taxIndex: number,
    checked: boolean,
  ) => void
}

const CategoryItem: FC<CategoryItemProps> = ({
  categoryIndex,
  register,
  errors,
  control,
  watch,
  setValue,
  hasEditPermission,
  onDeleteCategory,
  onSaveCategory,
  isSaving,
  onVatChange,
}) => {
  const {
    fields: taxFields,
    append: appendTax,
    remove: removeTax,
  } = useFieldArray({
    control,
    name: `categories.${categoryIndex}.taxes`,
  })

  const activeValue = watch(`categories.${categoryIndex}.active`)

  return (
    <div className="rounded-3xl border border-monochrome-3-60 p-[15px]">
      <div className="p-3 flex flex-col gap-3">
        <FormGroup label="Category Name">
          <Input
            isInvalid={!!errors?.categories?.[categoryIndex]?.name?.message}
            {...register(`categories.${categoryIndex}.name`)}
            placeholder="Enter Category Name"
            disabled={!hasEditPermission}
          />
        </FormGroup>
        <FormGroup
          label="Active"
          direction="row"
          labelClassName="whitespace-nowrap"
          inputWrapperClassName="w-auto"
          wrapperClassName="w-auto flex-row-reverse justify-end"
        >
          <Checkbox
            checked={activeValue}
            onCheckedChange={(checked) =>
              setValue(`categories.${categoryIndex}.active`, checked === true)
            }
            disabled={!hasEditPermission}
          />
        </FormGroup>

        {taxFields.map((taxField, taxIndex) => (
          <TaxItem
            key={taxField.id}
            categoryIndex={categoryIndex}
            taxIndex={taxIndex}
            register={register}
            errors={errors}
            watch={watch}
            setValue={setValue}
            hasEditPermission={hasEditPermission}
            onRemove={() => removeTax(taxIndex)}
            onVatChange={onVatChange}
          />
        ))}

        <Button
          variant="secondary"
          type="button"
          onClick={() =>
            appendTax({
              name: '',
              amountPercentage: null,
              amountFixed: null,
              vat: false,
              includeInInvoice: false,
              includedInTicketPrice: true,
            })
          }
          disabled={!hasEditPermission}
          className="self-center"
        >
          Add New Tax +
        </Button>
      </div>
      <div className="flex justify-end gap-2 p-3 border-t border-monochrome-120">
        <Button
          onClick={onDeleteCategory}
          variant="destructive"
          type="button"
          disabled={!hasEditPermission}
        >
          Delete Category
        </Button>
        <Button
          variant="primary"
          type="button"
          onClick={onSaveCategory}
          disabled={!hasEditPermission}
          loading={isSaving}
        >
          Save
        </Button>
      </div>
    </div>
  )
}
